/**
 * Compares two software version numbers (e.g. "1.7.1" or "1.2.42") and checks if the comparison specified by the operator holds true.
 *
 * @param {String} v1 - The first version number to compare.
 * @param {String} v2 - The second version number to compare.
 * @param {String} operator - The comparison operator to use. Can be one of '<', 'lt', '<=', 'le', '>', 'gt', '>=', 'ge', '=', '==', 'eq', '!=', '<>', 'ne'.
 * @return {Boolean} - The result of the comparison.
 */
const versionCompare = ( v1, v2, operator = '=' ) => {
	if ( typeof v1 !== 'string' || typeof v2 !== 'string' ) {
		throw new Error( 'Invalid version number' );
	}

	/**
	 * Inner function to compare version numbers.
	 * Splits version strings into arrays and compares each segment numerically.
	 * @return {Number} -1 if v1 < v2, 1 if v1 > v2, 0 if v1 == v2.
	 */
	const comparison = ( () => {
		// Splitting each version number into parts and converting them to numbers
		const parts1 = v1.split( '.' ).map( Number );
		const parts2 = v2.split( '.' ).map( Number );

		// Comparing each corresponding part of the version numbers
		for ( let i = 0; i < Math.max( parts1.length, parts2.length ); i++ ) {
			// Filling in missing parts with zeros for comparison
			const part1 = parts1[i] ?? 0;
			const part2 = parts2[i] ?? 0;

			// Determining the ordering of the two versions
			if ( part1 > part2 ) {
				return 1;
			}
			if ( part1 < part2 ) {
				return -1;
			}
		}

		// Versions are equal if no difference found in any parts
		return 0;
	} )();

	// Interpreting the result based on the specified comparison operator
	switch ( operator ) {
		case '<':
		case 'lt':
			return comparison < 0;

		case '<=':
		case 'le':
			return comparison <= 0;

		case '>':
		case 'gt':
			return comparison > 0;

		case '>=':
		case 'ge':
			return comparison >= 0;

		case '=':
		case '==':
		case 'eq':
			return comparison === 0;

		case '!=':
		case '<>':
		case 'ne':
			return comparison !== 0;

		default:
			// Handling of invalid comparison operators
			throw new Error( 'Invalid comparison operator' );
	}
}

export default versionCompare;